import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import axios from "axios";
import { server } from "../server";
import Loading from "../Components/global/Loading";
import { useDispatch, useSelector } from "react-redux";
import { getAllPricingsList } from "../redux/actions/contents";
import PageName from "../Components/global/PageName";
import {
  FaPlus,
  FaTrash,
  FaEdit,
  FaStar,
  FaTimes,
  FaTag,
  FaSearch,
  FaUndo,
  FaArrowLeft,
  FaArrowRight,
  FaCloudUploadAlt,
  FaFilter,
} from "react-icons/fa";
import { MdOutlineLocalOffer, MdOutlineCheck } from "react-icons/md";
import { GiSparkles } from "react-icons/gi";
import { uploadMediaToServer } from "../utils/uploadMediaToServer";

const defaultForm = {
  title: "",
  finish: "",
  bestFor: "",
  price: "",
  priceFormatted: "",
  icon: "star",
  image: "",
  description: "",
  features: [],
  popular: false,
};

export default function PricingManager() {
  const dispatch = useDispatch();
  const { pricingsList, pricingCategories, pricingsCount, loading: pricingsLoading } = useSelector(
    (state) => state?.content
  );

  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [sync, setSync] = useState(0);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(6);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(defaultForm);
  const [featureInput, setFeatureInput] = useState("");
  const [isCustomBestFor, setIsCustomBestFor] = useState(false);

  useEffect(() => {
    dispatch(getAllPricingsList(pageNo, pageSize, search, categoryFilter));
  }, [sync, search, categoryFilter, pageNo, pageSize, dispatch]);

  const totalCount = pricingsCount || 0;
  const totalPages = Math.ceil(totalCount / pageSize) || 1;
  const startItem = totalCount > 0 ? (pageNo - 1) * pageSize + 1 : 0;
  const endItem = Math.min(pageNo * pageSize, totalCount);

  const handleImageFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploadingImage(true);
      const url = await uploadMediaToServer(file, "null");
      if (url) {
        setFormData((prev) => ({ ...prev, image: url }));
        toast.success("Image uploaded successfully");
      }
    } catch (err) {
      toast.error("Failed to upload image");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleOpenCreateModal = () => {
    setEditingId(null);
    setFormData(defaultForm);
    setFeatureInput("");
    setIsCustomBestFor(false);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (item) => {
    setEditingId(item.id);
    setFormData({
      title: item.title || "",
      finish: item.finish || "",
      bestFor: item.bestFor || "",
      price: item.price !== null && item.price !== undefined ? item.price : "",
      priceFormatted: item.priceFormatted || "",
      icon: item.icon || "star",
      image: item.image || "",
      description: item.description || "",
      features: Array.isArray(item.features) ? [...item.features] : [],
      popular: !!item.popular,
    });
    setFeatureInput("");
    setIsCustomBestFor(false);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
    setFormData(defaultForm);
    setFeatureInput("");
    setIsCustomBestFor(false);
  };

  const handleAddFeature = () => {
    const trimmed = featureInput.trim();
    if (!trimmed) return;
    setFormData((prev) => ({
      ...prev,
      features: [...prev.features, trimmed],
    }));
    setFeatureInput("");
  };

  const handleRemoveFeature = (index) => {
    setFormData((prev) => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      return toast.error("Title is required");
    }

    try {
      setLoading(true);
      const payload = {
        id: editingId ? Number(editingId) : 0,
        title: formData.title.trim(),
        finish: formData.finish.trim(),
        bestFor: formData.bestFor.trim(),
        price: formData.price !== "" ? Number(formData.price) : 0,
        priceFormatted: formData.priceFormatted.trim(),
        icon: formData.icon.trim(),
        image: formData.image.trim(),
        description: formData.description.trim(),
        features: formData.features,
        popular: formData.popular,
        deleted: false,
      };

      const token = localStorage.getItem("token");

      if (editingId) {
        const { data: res } = await axios.put(
          `${server}/pricing/updateById`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            params: { id: editingId },
          }
        );
        toast.success(res?.message || "Pricing updated successfully");
      } else {
        const { data: res } = await axios.post(
          `${server}/pricing/new`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        toast.success(res?.message || "Pricing saved successfully");
      }

      handleCloseModal();
      setSync((prev) => prev + 1);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Operation failed");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this pricing plan?"
    );
    if (!confirmDelete) return;

    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const { data: res } = await axios.delete(
        `${server}/pricing/deleteById`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { id },
        }
      );
      toast.success(res?.message || "Pricing deleted successfully");
      setSync((prev) => prev + 1);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Delete failed");
    } finally {
      setLoading(false);
    }
  };

  const handleRecover = async (id) => {
    const confirmRecover = window.confirm(
      "Are you sure you want to recover this deleted pricing plan?"
    );
    if (!confirmRecover) return;

    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const { data: res } = await axios.post(
        `${server}/pricing/recoverDeletedById`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { id },
        }
      );
      toast.success(res?.message || "Pricing plan recovered successfully");
      setSync((prev) => prev + 1);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Recovery failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pb-16 bg-[#0A0A0C] text-[#F8F5F2]">
      <PageName
        title="Manage Pricing Plans"
        subTitle="Create, update, recover, and organize pricing packages in dark mode."
      />

      {/* Control Bar */}
      <div className="max-w-6xl mx-auto mt-6 px-4 flex flex-col md:flex-row items-center justify-between gap-4">
        
        <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
          {/* Search Input */}
          <div className="relative w-full sm:w-64">
            <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#C8A165]/60 text-xs" />
            <input
              type="text"
              placeholder="Search packages..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPageNo(1);
              }}
              className="w-full text-xs pl-10 pr-4 py-3 bg-[#121215] text-[#F8F5F2] border border-[#C8A165]/25 rounded-2xl focus:outline-none focus:border-[#C8A165] placeholder:text-[#F8F5F2]/30 shadow-lg transition-all"
            />
          </div>

          {/* Best For Category Filter Dropdown */}
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <span className="text-[0.68rem] uppercase font-bold text-[#C8A165] flex items-center gap-1 whitespace-nowrap">
              <FaFilter size={10} /> Best For:
            </span>
            <select
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value);
                setPageNo(1);
              }}
              className="w-full sm:w-auto text-xs px-3.5 py-2.5 bg-[#121215] text-[#F8F5F2] border border-[#C8A165]/25 rounded-2xl focus:outline-none focus:border-[#C8A165] shadow-lg cursor-pointer"
            >
              <option value="All">All Categories</option>
              {pricingCategories &&
                pricingCategories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
            </select>
          </div>
        </div>

        {/* Add New Package CTA */}
        <motion.button
          onClick={handleOpenCreateModal}
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          className="flex items-center gap-2 px-6 py-3 rounded-2xl font-bold text-xs uppercase tracking-widest shadow-lg text-[#0A0A0C] cursor-pointer"
          style={{
            background: "linear-gradient(135deg, #C8A165 0%, #F5E5C9 50%, #9E7A48 100%)",
          }}
        >
          <FaPlus size={12} /> Add New Pricing Package
        </motion.button>
      </div>

      {/* Category Filter Pills */}
      {pricingCategories && pricingCategories.length > 0 && (
        <div className="max-w-6xl mx-auto mt-4 px-4 flex flex-wrap items-center gap-2">
          <button
            onClick={() => {
              setCategoryFilter("All");
              setPageNo(1);
            }}
            className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all cursor-pointer ${
              categoryFilter === "All"
                ? "bg-[#C8A165] text-[#0A0A0C] font-bold shadow-md"
                : "bg-[#121215] text-[#F8F5F2]/60 hover:text-[#C8A165] border border-[#C8A165]/20"
            }`}
          >
            All Categories
          </button>
          {pricingCategories.map((cat) => (
            <button
              key={cat}
              onClick={() => {
                setCategoryFilter(cat);
                setPageNo(1);
              }}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all cursor-pointer ${
                categoryFilter === cat
                  ? "bg-[#C8A165] text-[#0A0A0C] font-bold shadow-md"
                  : "bg-[#121215] text-[#F8F5F2]/60 hover:text-[#C8A165] border border-[#C8A165]/20"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      )}

      {/* Pricing Cards Grid */}
      <div className="max-w-6xl mx-auto mt-8 px-4 flex flex-wrap justify-center gap-6 text-left">
        {pricingsList && pricingsList.length > 0 ? (
          pricingsList.map((item, index) => (
            <motion.div
              key={item.id || index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              className={`relative flex flex-col justify-between bg-[#121215]/90 backdrop-blur-2xl rounded-3xl p-6 shadow-[0_15px_40px_rgba(0,0,0,0.7)] border transition-all duration-300 hover:border-[#C8A165]/60 hover:shadow-[0_15px_40px_rgba(200,161,101,0.15)] w-full max-w-sm sm:w-[calc(50%-16px)] lg:w-[calc(33.333%-16px)] min-w-[280px] ${
                item.deleted
                  ? "opacity-60 border-rose-500/30 bg-rose-950/10"
                  : item.popular
                  ? "border-[#C8A165] ring-1 ring-[#C8A165]/50"
                  : "border-[#C8A165]/20"
              }`}
            >
              {/* Popular Badge */}
              {item.popular && !item.deleted && (
                <div className="absolute -top-3 right-4 bg-gradient-to-r from-[#C8A165] to-[#F5E5C9] text-[#0A0A0C] text-[0.62rem] font-extrabold px-3 py-1 rounded-full shadow-lg flex items-center gap-1 uppercase tracking-wider">
                  <FaStar size={10} /> Most Popular
                </div>
              )}

              {/* Deleted Badge */}
              {item.deleted && (
                <div className="absolute -top-3 left-4 bg-rose-600 text-white text-[0.62rem] font-bold px-3 py-1 rounded-full shadow flex items-center gap-1 uppercase">
                  Deleted
                </div>
              )}

              <div>
                {/* Image Banner */}
                {item.image && (
                  <div className="mb-4 h-40 w-full overflow-hidden rounded-2xl border border-[#C8A165]/20 bg-[#0A0A0C]">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
                    />
                  </div>
                )}

                {/* Header */}
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-[#F8F5F2] font-cormorant">
                      {item.title}
                    </h3>
                    {item.finish && (
                      <span className="inline-block mt-1 text-[0.62rem] font-semibold px-2.5 py-0.5 rounded-full bg-[#C8A165]/15 text-[#C8A165] border border-[#C8A165]/30">
                        {item.finish}
                      </span>
                    )}
                  </div>
                </div>

                {/* Price Display */}
                <div className="my-4">
                  <span className="text-3xl font-extrabold text-[#C8A165] font-poppins">
                    {item.priceFormatted || `₹${item.price ?? 0}`}
                  </span>
                </div>

                {/* Description */}
                {item.description && (
                  <p className="mb-3 text-xs text-[#F8F5F2]/60 line-clamp-2 leading-relaxed font-poppins">
                    {item.description}
                  </p>
                )}

                {/* Best For */}
                {item.bestFor && (
                  <div className="mb-4 text-xs text-[#F8F5F2]/70 flex items-center gap-1.5 bg-[#18181D] p-2.5 rounded-xl border border-[#C8A165]/15">
                    <MdOutlineLocalOffer className="text-[#C8A165] text-sm shrink-0" />
                    <span>
                      <strong className="text-[#C8A165]">Best for:</strong>{" "}
                      {item.bestFor}
                    </span>
                  </div>
                )}

                {/* Features List */}
                {Array.isArray(item.features) && item.features.length > 0 && (
                  <div className="mt-4 border-t border-[#C8A165]/15 pt-4">
                    <p className="text-[0.62rem] font-bold text-[#C8A165] uppercase tracking-wider mb-2.5">
                      Package Features
                    </p>
                    <ul className="space-y-2 text-xs text-[#F8F5F2]/80">
                      {item.features.map((feat, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <MdOutlineCheck className="text-[#C8A165] text-base shrink-0 mt-0.5" />
                          <span>{feat}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="mt-6 pt-4 border-t border-[#C8A165]/15 flex items-center justify-end gap-2">
                <button
                  onClick={() => handleOpenEditModal(item)}
                  className="px-3.5 py-1.5 text-xs font-bold text-[#C8A165] bg-[#C8A165]/15 hover:bg-[#C8A165] hover:text-[#0A0A0C] border border-[#C8A165]/30 rounded-xl flex items-center gap-1.5 transition-all cursor-pointer"
                >
                  <FaEdit size={11} /> Edit
                </button>

                {item.deleted ? (
                  <button
                    onClick={() => handleRecover(item.id)}
                    className="px-3.5 py-1.5 text-xs font-bold text-emerald-400 bg-emerald-500/15 hover:bg-emerald-500 hover:text-[#0A0A0C] border border-emerald-500/30 rounded-xl flex items-center gap-1.5 transition-all cursor-pointer"
                  >
                    <FaUndo size={11} /> Recover
                  </button>
                ) : (
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="px-3.5 py-1.5 text-xs font-bold text-rose-400 bg-rose-500/15 hover:bg-rose-600 hover:text-white border border-rose-500/30 rounded-xl flex items-center gap-1.5 transition-all cursor-pointer"
                  >
                    <FaTrash size={11} /> Delete
                  </button>
                )}
              </div>
            </motion.div>
          ))
        ) : (
          <div className="col-span-full py-16 text-center text-[#F8F5F2]/40 bg-[#121215] rounded-3xl border border-dashed border-[#C8A165]/20 w-full p-8">
            <FaTag className="mx-auto text-4xl mb-3 text-[#C8A165]/40 animate-pulse" />
            <p className="text-base font-bold text-[#F8F5F2]">No Pricing Plans Found</p>
            <p className="text-xs text-[#F8F5F2]/50 mt-1">
              Click &quot;Add New Pricing Package&quot; to create your first package.
            </p>
          </div>
        )}
      </div>

      {/* Pagination Bar */}
      {totalCount > 0 && (
        <div className="max-w-6xl mx-auto mt-8 px-4 flex flex-col sm:flex-row items-center justify-between gap-4 bg-[#121215]/90 backdrop-blur-2xl p-4 rounded-2xl border border-[#C8A165]/25 shadow-lg">
          <div className="text-xs text-[#F8F5F2]/60 font-medium">
            Showing <span className="font-bold text-[#C8A165]">{startItem}</span> to{" "}
            <span className="font-bold text-[#C8A165]">{endItem}</span> of{" "}
            <span className="font-bold text-[#C8A165]">{totalCount}</span> pricing plans
          </div>

          <div className="flex items-center gap-4">
            {/* Per Page Selector */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-[#F8F5F2]/50 font-medium">Per page:</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPageNo(1);
                }}
                className="text-xs border border-[#C8A165]/30 rounded-xl px-2.5 py-1 bg-[#18181D] text-[#F8F5F2] focus:outline-none focus:border-[#C8A165]"
              >
                <option value={2} className="bg-[#18181D]">2</option>
                <option value={6} className="bg-[#18181D]">6</option>
                <option value={12} className="bg-[#18181D]">12</option>
                <option value={24} className="bg-[#18181D]">24</option>
                <option value={50} className="bg-[#18181D]">50</option>
              </select>
            </div>

            {/* Pagination Controls */}
            <div className="flex items-center gap-1.5">
              <button
                disabled={pageNo <= 1}
                onClick={() => setPageNo((prev) => Math.max(1, prev - 1))}
                className="px-3 py-1.5 text-xs font-bold rounded-xl border border-[#C8A165]/30 bg-[#18181D] text-[#C8A165] hover:bg-[#C8A165] hover:text-[#0A0A0C] disabled:opacity-30 disabled:pointer-events-none transition-all cursor-pointer flex items-center gap-1"
              >
                <FaArrowLeft size={10} /> Prev
              </button>

              <div className="flex items-center gap-1 px-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => (
                  <button
                    key={num}
                    onClick={() => setPageNo(num)}
                    className={`w-7 h-7 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                      pageNo === num
                        ? "bg-[#C8A165] text-[#0A0A0C] font-extrabold shadow-md"
                        : "bg-[#18181D] text-[#F8F5F2]/70 hover:text-[#C8A165] border border-[#C8A165]/20"
                    }`}
                  >
                    {num}
                  </button>
                ))}
              </div>

              <button
                disabled={pageNo >= totalPages}
                onClick={() => setPageNo((prev) => Math.min(totalPages, prev + 1))}
                className="px-3 py-1.5 text-xs font-bold rounded-xl border border-[#C8A165]/30 bg-[#18181D] text-[#C8A165] hover:bg-[#C8A165] hover:text-[#0A0A0C] disabled:opacity-30 disabled:pointer-events-none transition-all cursor-pointer flex items-center gap-1"
              >
                Next <FaArrowRight size={10} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Dialog for Add / Edit */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-[#121215] w-full max-w-2xl rounded-3xl border border-[#C8A165]/30 shadow-[0_20px_50px_rgba(0,0,0,0.9)] overflow-hidden flex flex-col max-h-[90vh] text-left"
            >
              {/* Modal Header */}
              <div className="px-6 py-4 border-b border-[#C8A165]/20 flex items-center justify-between bg-[#18181D]">
                <h3 className="text-base font-bold text-[#F8F5F2] flex items-center gap-2">
                  <GiSparkles className="text-[#C8A165]" />
                  {editingId ? "Edit Pricing Package" : "Add New Pricing Package"}
                </h3>
                <button
                  onClick={handleCloseModal}
                  className="w-8 h-8 rounded-full bg-white/5 border border-white/10 text-cream/70 hover:text-gold flex items-center justify-center transition-colors cursor-pointer"
                >
                  <FaTimes size={14} />
                </button>
              </div>

              {/* Modal Body / Form */}
              <form
                onSubmit={handleSubmit}
                className="p-6 overflow-y-auto space-y-4 flex-1 text-left"
              >
                {/* Title & Finish */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-[#C8A165] mb-1.5">
                      Package Title <span className="text-rose-400">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Standard Album Package"
                      value={formData.title}
                      onChange={(e) =>
                        setFormData({ ...formData, title: e.target.value })
                      }
                      className="w-full text-xs p-3 bg-[#18181D] text-[#F8F5F2] border border-[#C8A165]/25 rounded-2xl focus:outline-none focus:border-[#C8A165] placeholder:text-[#F8F5F2]/30"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#C8A165] mb-1.5">
                      Finish Type
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Glossy Finish / HD Velvet"
                      value={formData.finish}
                      onChange={(e) =>
                        setFormData({ ...formData, finish: e.target.value })
                      }
                      className="w-full text-xs p-3 bg-[#18181D] text-[#F8F5F2] border border-[#C8A165]/25 rounded-2xl focus:outline-none focus:border-[#C8A165] placeholder:text-[#F8F5F2]/30"
                    />
                  </div>
                </div>

                {/* Best For & Price */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-1">
                    <label className="block text-xs font-semibold text-[#C8A165] mb-1.5">
                      Best For
                    </label>
                    {(() => {
                      const availableOptions = Array.from(
                        new Set([
                          ...(pricingCategories || []),
                          ...(formData.bestFor && formData.bestFor !== "__custom__" ? [formData.bestFor] : []),
                        ])
                      ).filter(Boolean);

                      return (
                        <>
                          <select
                            value={isCustomBestFor ? "__custom__" : formData.bestFor}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === "__custom__") {
                                setIsCustomBestFor(true);
                                setFormData((prev) => ({ ...prev, bestFor: "" }));
                              } else {
                                setIsCustomBestFor(false);
                                setFormData((prev) => ({ ...prev, bestFor: val }));
                              }
                            }}
                            className="w-full text-xs p-3 bg-[#18181D] text-[#F8F5F2] border border-[#C8A165]/25 rounded-2xl focus:outline-none focus:border-[#C8A165] cursor-pointer"
                          >
                            <option value="">Select Best For...</option>
                            {availableOptions.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                            <option value="__custom__">+ Add Custom Best For...</option>
                          </select>

                          {isCustomBestFor && (
                            <input
                              type="text"
                              placeholder="Type new Best For category..."
                              value={formData.bestFor}
                              onChange={(e) =>
                                setFormData((prev) => ({ ...prev, bestFor: e.target.value }))
                              }
                              className="w-full text-xs p-3 mt-2 bg-[#18181D] text-[#F8F5F2] border border-[#C8A165]/25 rounded-2xl focus:outline-none focus:border-[#C8A165] placeholder:text-[#F8F5F2]/30"
                            />
                          )}
                        </>
                      );
                    })()}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#C8A165] mb-1.5">
                      Numeric Price
                    </label>
                    <input
                      type="number"
                      placeholder="e.g. 25000"
                      value={formData.price}
                      onChange={(e) =>
                        setFormData({ ...formData, price: e.target.value })
                      }
                      className="w-full text-xs p-3 bg-[#18181D] text-[#F8F5F2] border border-[#C8A165]/25 rounded-2xl focus:outline-none focus:border-[#C8A165] placeholder:text-[#F8F5F2]/30"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#C8A165] mb-1.5">
                      Formatted Price (Display)
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. ₹25,000 / event"
                      value={formData.priceFormatted}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          priceFormatted: e.target.value,
                        })
                      }
                      className="w-full text-xs p-3 bg-[#18181D] text-[#F8F5F2] border border-[#C8A165]/25 rounded-2xl focus:outline-none focus:border-[#C8A165] placeholder:text-[#F8F5F2]/30"
                    />
                  </div>
                </div>

                {/* Banner Image Upload & Preview */}
                <div>
                  <label className="block text-xs font-semibold text-[#C8A165] mb-1.5 flex items-center gap-1">
                    <FaCloudUploadAlt /> Package Banner Image
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageFileChange}
                    className="w-full text-xs p-2.5 bg-[#18181D] text-[#F8F5F2] border border-[#C8A165]/25 rounded-2xl focus:outline-none focus:border-[#C8A165] file:mr-3 file:py-1 file:px-3 file:rounded-xl file:border-0 file:text-[0.65rem] file:font-semibold file:bg-[#C8A165]/20 file:text-[#C8A165]"
                  />
                  {uploadingImage && (
                    <p className="text-xs text-[#C8A165] mt-1">Uploading image...</p>
                  )}
                  {formData.image && (
                    <div className="mt-3 relative rounded-2xl border border-[#C8A165]/30 overflow-hidden">
                      <img
                        src={formData.image}
                        alt="Package banner"
                        className="w-full h-36 object-cover"
                      />
                    </div>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-xs font-semibold text-[#C8A165] mb-1.5">
                    Description
                  </label>
                  <textarea
                    rows={3}
                    placeholder="Enter package summary..."
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    className="w-full text-xs p-3 bg-[#18181D] text-[#F8F5F2] border border-[#C8A165]/25 rounded-2xl focus:outline-none focus:border-[#C8A165] placeholder:text-[#F8F5F2]/30"
                  />
                </div>

                {/* Features Builder */}
                <div>
                  <label className="block text-xs font-semibold text-[#C8A165] mb-1.5">
                    Package Features
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Add feature item..."
                      value={featureInput}
                      onChange={(e) => setFeatureInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddFeature();
                        }
                      }}
                      className="flex-1 text-xs p-3 bg-[#18181D] text-[#F8F5F2] border border-[#C8A165]/25 rounded-2xl focus:outline-none focus:border-[#C8A165] placeholder:text-[#F8F5F2]/30"
                    />
                    <button
                      type="button"
                      onClick={handleAddFeature}
                      className="px-4 py-2 bg-[#C8A165]/20 hover:bg-[#C8A165] text-[#C8A165] hover:text-[#0A0A0C] border border-[#C8A165]/30 font-bold text-xs rounded-2xl transition-all cursor-pointer"
                    >
                      Add
                    </button>
                  </div>

                  {formData.features?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {formData.features.map((feat, i) => (
                        <span
                          key={i}
                          className="bg-[#18181D] border border-[#C8A165]/30 text-xs px-3 py-1 rounded-xl flex items-center gap-2 text-[#F8F5F2]"
                        >
                          <span>{feat}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveFeature(i)}
                            className="text-red-400 hover:text-red-300 cursor-pointer"
                          >
                            <FaTimes size={10} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Popular Checkbox */}
                <div className="flex items-center gap-2.5 pt-2">
                  <input
                    type="checkbox"
                    id="popular"
                    checked={formData.popular}
                    onChange={(e) =>
                      setFormData({ ...formData, popular: e.target.checked })
                    }
                    className="w-4 h-4 rounded text-[#C8A165] focus:ring-[#C8A165] accent-[#C8A165] bg-[#18181D]"
                  />
                  <label htmlFor="popular" className="text-xs font-semibold text-[#F8F5F2]">
                    Mark as &quot;Most Popular&quot; Package
                  </label>
                </div>

                {/* Submit Action */}
                <div className="pt-4 flex justify-end gap-3 border-t border-[#C8A165]/15">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-5 py-2.5 rounded-2xl border border-white/10 text-cream/70 hover:bg-white/5 text-xs font-bold transition-all cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-2.5 rounded-2xl font-bold text-xs uppercase tracking-wider text-[#0A0A0C] shadow-lg transition-all cursor-pointer disabled:opacity-50"
                    style={{
                      background: "linear-gradient(135deg, #C8A165 0%, #F5E5C9 50%, #9E7A48 100%)",
                    }}
                  >
                    {editingId ? "Save Changes" : "Create Package"}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {(loading || pricingsLoading) && <Loading />}
    </div>
  );
}
